import React, { useState } from 'react';
import {
  exportKeysByPartnerByStatusPivot,
  loadKeysByPartnerByTagByStatusPivot,
} from '../../../actions/reportActions';
import { SimpleTextFilter, Table, TableColumn, getVisibleColumns } from '../../common/Table';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import { useTranslation } from 'react-i18next';
import GameLink from '../../common/links/GameLink';
import CompanyLink from '../../common/links/CompanyLink';
import SKULink from '../../common/links/SKULink';
import { hasPermission } from '../../auth/authUtils';
import permissionTypes from '../../../constants/permissionTypes';
import { NavLink, useLocation } from 'react-router-dom';
import { scopes as tableScopes } from '../../../constants/scopes';

export interface KeysByCompanyByTagByStatusListProps {
  currentUser?: any;
}

function KeysByCompanyByTagByStatusList({ currentUser }: KeysByCompanyByTagByStatusListProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const allColumns = [
    'companyName',
    'game',
    'sku',
    'tag',
    'allocated',
    'sold',
    'activated',
    'disabled',
    'expired',
  ];
  const defaultVisibleColumns = allColumns;
  const [reload] = useState<any>();
  const [items, setItems] = useState<any>();
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
  } = useTableSettings(
    'keysbycompanybytagbystatusreport-settings',
    getTableSettingsDefaults({
      defaultSort: 'companyName',
      defaultVisibleColumns,
    }),
    null,
    loadKeysByPartnerByTagByStatusPivot,
    setItems,
    reload,
    t('Failed to load report')
  );

  let columnsConfig: TableColumn[] = [
    {
      Key: 'companyName',
      Label: t('Company'),
      Render: (row: any) => (
        <CompanyLink
          id={row.companyId}
          name={row.companyName}
        />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'game',
      Label: t('Title'),
      Render: (row: any) => (
        <GameLink
          gameId={row.gameId}
          gameTitle={row.game}
          currentUser={currentUser}
        />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'sku',
      Label: t('SKU'),
      Render: (row: any) => (
        <SKULink
          skuId={row.skuId}
          skuName={row.sku}
          currentUser={currentUser}
        />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    { Key: 'tag', Label: t('Tag') },
    {
      Key: 'allocated',
      Label: t('Allocated'),
      className: 'text-right num no-wrap',
      Render: (row: any) => CommaNumber_NoDecimal(row.allocated),
    },
    {
      Key: 'sold',
      Label: t('Sold'),
      className: 'text-right num no-wrap',
      Render: (row: any) =>
        hasPermission(currentUser, permissionTypes.MANAGE_KEYS) ? (
          <NavLink
            to={`/keys/reported-sold?${tableScopes.SKU}=${row.skuId}&${tableScopes.COMPANY}=${row.companyId}`}
          >
            {CommaNumber_NoDecimal(row.sold)}
          </NavLink>
        ) : (
          CommaNumber_NoDecimal(row.sold)
        ),
    },
    {
      Key: 'activated',
      Label: t('Activated'),
      className: 'text-right num no-wrap',
      Render: (row: any) =>
        hasPermission(currentUser, permissionTypes.MANAGE_KEYS) ? (
          <NavLink
            to={`/keys/reported-activated/?${tableScopes.SKU}=${row.skuId}&${tableScopes.COMPANY}=${row.companyId}`}
          >
            {CommaNumber_NoDecimal(row.activated)}
          </NavLink>
        ) : (
          CommaNumber_NoDecimal(row.activated)
        ),
    },
    {
      Key: 'disabled',
      Label: t('Disabled'),
      className: 'text-right num no-wrap',
      Render: (row: any) =>
        hasPermission(currentUser, permissionTypes.MANAGE_KEYS) ? (
          <NavLink
            to={`/keys/reported-disabled/?${tableScopes.SKU}=${row.skuId}&${tableScopes.COMPANY}=${row.companyId}`}
          >
            {CommaNumber_NoDecimal(row.disabled)}
          </NavLink>
        ) : (
          CommaNumber_NoDecimal(row.disabled)
        ),
    },
    {
      Key: 'expired',
      Label: t('Expired'),
      className: 'text-right num no-wrap',
      Render: (row: any) => CommaNumber_NoDecimal(row.expired),
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return items ? (
    <KwPanel className="mb-4">
      <Table
        className="table-v-stripes"
        columns={columns}
        allColumns={allColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        rows={items.data.map((x: any, i: number) => ({
          Key: `${i}${x.companyId}|${x.skuId}|${x.gameId}`,
          ...x,
        }))}
        totalRowCount={items.totalRowCount}
        filteredRowCount={items.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportKeysByPartnerByStatusPivot}
      />
    </KwPanel>
  ) : (
    <Loading></Loading>
  );
}

export default KeysByCompanyByTagByStatusList;
